// =============================================================================
// app/src/lib/cadastroDoBalcao.ts — a parte pura do cadastro pelo balcão e da
// fila de conferência (093, PR 2 — 08/10/2026).
//
// O mesmo formulário serve a duas telas: o colaborador cadastra a peça
// (/cadastro/balcao) e a gerência corrige antes de liberar (/conferencia). As
// réguas e o "como se lê" da rasura moram aqui, onde um teste enxerga — e o
// banco repete as mesmas réguas (validar_cadastro_do_balcao), porque tela é
// conveniência e quem nega é o Postgres.
// =============================================================================
import type { CampoDoCadastroDoBalcao, LinhaDaConferencia, PartType, StockMovement } from '@core/types';
import type { DadosDoCadastroDoBalcao } from './consultas';
import { brl, qtd } from './formato';

/** O formulário como a pessoa o vê. `tipo` null = ainda não escolheu (vale a
 *  sugestão pelo nome); '' = "Sem tipo" de propósito; slug = o escolhido. */
export interface RascunhoDoBalcao {
  descricao: string;
  codigo: string;
  marca: string;
  tipo: string | null;
  preco: number | null;
  local: string;
  quantidade: number | null;
}

export const RASCUNHO_VAZIO: RascunhoDoBalcao = {
  descricao: '', codigo: '', marca: '', tipo: null, preco: null, local: '', quantidade: null,
};

/** Acima disto o botão pergunta "confere?" antes de gravar: R$ 1.500 digitado
 *  no lugar de R$ 15,00 é o erro de vírgula que a regra 9 já conhece. */
export const PRECO_PARA_CONFIRMAR = 1000;

/** O tipo que vale: o escolhido, senão a sugestão pelo nome. "Sem tipo" ('')
 *  escolhido de propósito vence a sugestão. */
export function tipoQueVale(r: RascunhoDoBalcao, sugerido: string | null): string | null {
  if (r.tipo !== null) return r.tipo || null;
  return sugerido;
}

const texto = (v: string) => (v.trim() ? v.trim() : null);

/** O que vai ao banco. O lugar segue como foi digitado: quem normaliza
 *  (maiúscula, sem espaço) é a RPC, igual ao mapa. */
export function dadosDoRascunho(r: RascunhoDoBalcao, sugerido: string | null): DadosDoCadastroDoBalcao {
  return {
    descricao: r.descricao.trim(),
    marca: texto(r.marca),
    tipo: tipoQueVale(r, sugerido),
    codigo: texto(r.codigo),
    preco_balcao: r.preco,
    local: texto(r.local),
    quantidade: r.quantidade,
  };
}

/** A recusa ANTES do banco, na mesma língua dele — para a pessoa não esperar
 *  a viagem para saber que faltou o preço. Nulo = pode mandar. */
export function problemaDoRascunho(r: RascunhoDoBalcao): string | null {
  if (r.descricao.replace(/[^\p{L}\p{N}]/gu, '').length < 3) {
    return 'Escreva o nome da peça (pelo menos 3 letras ou números).';
  }
  if (r.preco === null || r.preco <= 0) {
    return 'Diga o preço de balcão: sem preço a peça não tem como ser vendida.';
  }
  if (r.preco > 100_000) return 'Preço de balcão acima de R$ 100.000,00: confira se não sobrou um zero.';
  if (r.quantidade !== null && r.quantidade < 0) return 'A quantidade na prateleira não pode ser negativa.';
  return null;
}

/** O saldo de verdade da peça HOJE, como a fila o trouxe. Na pendente que só
 *  o vendedor tocou é 0 (a contagem dele é declaração). */
export function saldoAtual(l: Pick<LinhaDaConferencia, 'quantidade'>): number {
  const n = Number(l.quantidade ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/** A peça já tem saldo CONTADO — e então o "Liberar" o mantém, a declaração do
 *  balcão não passa por cima. Quem decide é o banco (habilitar_peca: saldo
 *  diferente de 0 OU algum movimento de estoque), e a fila devolve a MESMA
 *  régua pronta em `saldo_contado`; a tela não refaz a conta. Inclui o saldo 0
 *  contado: a gerência contou a prateleira vazia, e liberar a deixa vazia
 *  (2ª revisão, 09/10/2026 — com "saldo > 0" o cartão calava esse caso). */
export function saldoContado(l: Pick<LinhaDaConferencia, 'saldo_contado'>): boolean {
  return l.saldo_contado === true;
}

/** A linha da fila como o formulário de correção começa — os valores que o
 *  banco tem HOJE, e a quantidade que o colaborador DECLAROU. Não o saldo:
 *  quem decide se a declaração ainda vale é o banco, no instante do toque
 *  (habilitar_peca). A peça com saldo contado mantém o saldo quando chega a
 *  declaração; um número diferente dela é contagem da gerência e vira saldo. */
export function rascunhoDaLinha(l: LinhaDaConferencia): RascunhoDoBalcao {
  return {
    descricao: l.descricao,
    codigo: l.codigo_fabrica ?? '',
    marca: l.marca ?? '',
    // Explícito: corrigir não troca o tipo pelo nome sem ninguém ver.
    tipo: l.tipo ?? '',
    preco: l.preco_balcao === null ? null : Number(l.preco_balcao),
    local: l.local ?? '',
    quantidade: contagemDeclarada(l),
  };
}

/** "Liberar" de um toque (decisão b): os valores do cartão como estão e a
 *  quantidade DECLARADA. O banco decide: sem saldo contado, ela vira o saldo;
 *  com saldo contado, o saldo fica.
 *
 *  Por que não o saldo que o cartão mostra (2ª revisão, 09/10/2026): o cartão
 *  pode estar velho. Abriu com saldo 5, o dono recontou 7, e o toque no cartão
 *  velho mandava 5 — número diferente da declaração, que o banco lê como
 *  contagem nova da gerência: o saldo voltava para 5. A declaração não muda
 *  nunca, então mandá-la é a única coisa que um cartão velho não estraga. */
export function dadosDaLinha(l: LinhaDaConferencia): DadosDoCadastroDoBalcao {
  return dadosDoRascunho(rascunhoDaLinha(l), null);
}

/** Quanto o colaborador disse que contou — DECLARAÇÃO, não saldo. */
export function contagemDeclarada(l: Pick<LinhaDaConferencia, 'original'>): number | null {
  const n = l.original.quantidade_contada;
  return n === undefined ? null : Number(n);
}

// --- A contagem do balcão e a nota da mesma caixa --------------------------------
// Achado da revisão (09/10/2026): o vendedor cadastra a peça a partir da caixa
// que acabou de chegar e declara 5; o gerente libera em um toque (o saldo vai
// a 5) e DEPOIS lança a nota da mesma caixa na Entrada — que soma: 10. A
// entrada que vem ANTES da liberação não duplica (ela libera sem lançar a
// contagem); a ordem inversa, que o sino favorece, sim.
//
// Só quem segura a caixa sabe se a nota é dela, então a Entrada AVISA, e não
// adivinha. O sinal: o último movimento de compra ou contagem da peça ainda é
// a contagem do balcão. As MARCAS abaixo são a observação que conferencia.sql
// escreve nesse movimento — o teste lê o .sql e confere que as duas estão lá,
// e a varredura (§7b) prende o lado do banco.

/** liberar_peca_interna: a declaração que virou saldo no "Liberar". */
export const MARCA_DA_LIBERACAO = 'primeira contagem — liberação';
/** cadastrar_peca_pelo_balcao: a contagem do gerente ou do dono no cadastro. */
export const MARCA_DO_CADASTRO = 'contagem no cadastro pelo balcão';

export interface ContagemDoBalcao {
  origem: 'liberacao' | 'cadastro';
  /** O saldo que a contagem deixou. */
  quantidade: number;
  em: string;
}

/** O saldo ainda é a contagem do balcão, sem nota nem contagem da gerência
 *  depois? Recebe o ÚLTIMO movimento de compra/contagem (ultimaCompraOuContagem).
 *  Nulo = nada a avisar.
 *
 *  Saldo 0 também é nada a avisar (2ª revisão, 09/10/2026): a gerência que
 *  contou e depois corrigiu para 0 na fila deixa a marca da liberação com
 *  saldo_apos 0. Não há unidade nenhuma que a nota pudesse contar duas vezes, e
 *  o aviso diria "0 un contadas no balcão... já estão no estoque". */
export function contagemDoBalcaoSemEntrada(
  m: Pick<StockMovement, 'reason' | 'observacao' | 'saldo_apos' | 'created_at'> | null | undefined,
): ContagemDoBalcao | null {
  if (!m || m.reason !== 'inventario' || !m.observacao) return null;
  const origem = m.observacao.startsWith(MARCA_DA_LIBERACAO) ? 'liberacao'
    : m.observacao.startsWith(MARCA_DO_CADASTRO) ? 'cadastro'
    : null;
  if (origem === null) return null;
  const quantidade = Number(m.saldo_apos ?? 0);
  if (!(quantidade > 0)) return null;
  return { origem, quantidade, em: m.created_at };
}

// --- A RASURA ------------------------------------------------------------------

const ROTULOS: Record<CampoDoCadastroDoBalcao, string> = {
  descricao: 'Nome',
  marca: 'Marca',
  tipo: 'Tipo',
  codigo_fabrica: 'Código',
  preco_balcao: 'Preço de balcão',
  local: 'Onde fica',
  quantidade_contada: 'Quantidade',
};

/** A ordem em que a rasura se lê: a mesma do formulário. */
export const ORDEM_DOS_CAMPOS: CampoDoCadastroDoBalcao[] = [
  'descricao', 'codigo_fabrica', 'marca', 'tipo', 'preco_balcao', 'local', 'quantidade_contada',
];

export const rotuloDoCampo = (campo: string): string =>
  ROTULOS[campo as CampoDoCadastroDoBalcao] ?? campo;

/** Um valor da rasura como se lê: dinheiro em reais, tipo pelo nome. */
export function valorLegivel(
  campo: string, valor: string | number | null | undefined, tipos: ReadonlyArray<Pick<PartType, 'slug' | 'nome'>> = [],
): string {
  if (valor === null || valor === undefined || valor === '') return '(vazio)';
  if (campo === 'preco_balcao') return brl(Number(valor));
  if (campo === 'quantidade_contada') return `${qtd(Number(valor))} un`;
  if (campo === 'tipo') return tipos.find((t) => t.slug === valor)?.nome ?? String(valor);
  return String(valor);
}

/** As correções em ordem de leitura, cada uma com o rótulo e o de/para. */
export function linhasDaRasura(
  correcoes: LinhaDaConferencia['correcoes'],
  tipos: ReadonlyArray<Pick<PartType, 'slug' | 'nome'>> = [],
): Array<{ campo: string; rotulo: string; de: string; para: string }> {
  const chaves = Object.keys(correcoes ?? {});
  const ordem = (c: string) => {
    const i = ORDEM_DOS_CAMPOS.indexOf(c as CampoDoCadastroDoBalcao);
    return i < 0 ? ORDEM_DOS_CAMPOS.length : i;
  };
  return chaves
    .sort((a, b) => ordem(a) - ordem(b))
    .map((campo) => {
      const c = correcoes[campo as CampoDoCadastroDoBalcao];
      return {
        campo,
        rotulo: rotuloDoCampo(campo),
        de: valorLegivel(campo, c?.de, tipos),
        para: valorLegivel(campo, c?.para, tipos),
      };
    });
}
